import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { storesService } from "../services/stores";
import { benefitsService } from "../services/benefits";
import { API_BASE_URL } from "../config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import FeaturedStoreCard from "../components/FeaturedStoreCard";
import LandingNavbar from "../components/ui/LandingNavbar";
import BrandScroller from "../components/ui/BrandScroller";

const StoresPreview = () => {
  const navigate = useNavigate();
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [participatingStores, setParticipatingStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [loadingBenefits, setLoadingBenefits] = useState(true);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loadingFeaturedStores, setLoadingFeaturedStores] = useState(true);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const stores = await storesService.getParticipatingStores();
        setParticipatingStores(stores);
      } catch (err) {
        console.error("Error loading stores:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const loadBenefits = async () => {
      try {
        const response = await benefitsService.getAll();
        if (response.success && response.data) setBenefits(response.data);
      } catch (error) {
        console.error("Error cargando beneficios:", error);
      } finally {
        setLoadingBenefits(false);
      }
    };
    loadBenefits();
  }, []);

  useEffect(() => {
    const loadFeaturedStores = async () => {
      try {
        const stores = await storesService.getFeaturedStores();
        setFeaturedStores(stores);
      } catch (error) {
        console.error("Error cargando tiendas destacadas:", error);
      } finally {
        setLoadingFeaturedStores(false);
      }
    };
    loadFeaturedStores();
  }, []);

  const getStoreLogo = (benefit) => {
    if (benefit.storeLogo) return benefit.storeLogo;
    const store = participatingStores.find(
      (s) => s.id === benefit.storeId || s.id === String(benefit.storeId)
    );
    if (store?.logo) {
      return store.logo.startsWith("http")
        ? store.logo
        : `${API_BASE_URL}${store.logo.startsWith("/") ? "" : "/"}${store.logo}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <LandingNavbar onBack={() => navigate("/")} onLogin={() => navigate("/login")} />
      <BrandScroller />

      <div className="px-6 py-10 max-w-4xl mx-auto">

        {/* ── Sección: TIENDAS Y BENEFICIOS ── */}
        <div className="mb-10">
          {/* Título */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <h2 className="text-black font-bold tracking-tight text-2xl">
              TIENDAS Y BENEFICIOS
            </h2>
          </div>
          <p className="text-center text-gray-400 text-[10px] font-semibold tracking-[0.14em] uppercase mb-8">
            ESTOS SON LOS BENEFICIOS ACTUALES DE VONDI
          </p>

          {/* Lista de beneficios */}
          {loadingBenefits ? (
            <div className="text-center py-10">
              <p className="text-gray-500">Cargando beneficios...</p>
            </div>
          ) : benefits.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No hay beneficios disponibles</p>
            </div>
          ) : (
            <div className="space-y-4">
              {benefits.map((benefit) => (
                <div
                  key={benefit.id}
                  onClick={() => setSelectedBenefit(benefit)}
                  className="bg-white border border-black/8 rounded-3xl p-5 relative cursor-pointer hover:border-black/20 hover:shadow-sm transition-all hover:scale-[1.01] active:scale-[0.99]"
                >
                  {/* Logo esquina superior derecha */}
                  {getStoreLogo(benefit) && (
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-xl overflow-hidden bg-black/5 flex items-center justify-center">
                      <img
                        src={getStoreLogo(benefit)}
                        alt={benefit.storeName || "Logo"}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}

                  {/* Nombre de tienda */}
                  <p className="text-gray-400 text-[10px] font-semibold tracking-widest uppercase mb-3 pr-14">
                    {benefit.storeName || "Tienda"}
                  </p>

                  {/* Chip oscuro con el beneficio */}
                  <div
                    className="bg-black rounded-2xl px-4 py-2.5"
                    style={{ maxWidth: "85%" }}
                  >
                    <p className="text-white text-sm font-semibold leading-tight">
                      {benefit.title.toUpperCase()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Sección: TIENDAS DESTACADAS ── */}
        <div className="mb-12">
          {/* Título */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <h2 className="text-black font-bold tracking-tight text-2xl">
              TIENDAS DESTACADAS
            </h2>
          </div>

          {loadingFeaturedStores ? (
            <div className="text-center py-10">
              <p className="text-gray-500">Cargando tiendas destacadas...</p>
            </div>
          ) : featuredStores.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No hay tiendas destacadas disponibles</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredStores.map((store, index) => {
                const variant = index % 2 === 0 ? "left" : "right";
                let logoUrl = null;
                if (store.logo) {
                  logoUrl = store.logo.startsWith("http")
                    ? store.logo
                    : `${API_BASE_URL}${store.logo.startsWith("/") ? "" : "/"}${store.logo}`;
                }
                return (
                  <FeaturedStoreCard
                    key={store.id}
                    variant={variant}
                    imageUrl={logoUrl}
                    alt={store.name || "Logo de tienda"}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ── CTA ── */}
        <div className="text-center pb-8">
          <p className="text-gray-500 text-sm mb-4">
            ¿Quieres disfrutar de todos estos beneficios?
          </p>
          <Button
            onClick={() => navigate("/signup")}
            className="bg-black text-white hover:bg-black/90 rounded-full px-10 py-6 text-base font-semibold transition-all hover:scale-105 active:scale-95"
          >
            Conviértete en miembro VONDI
          </Button>
        </div>
      </div>

      {/* ── Dialog: Detalle de tienda ── */}
      <Dialog open={!!selectedStore} onOpenChange={() => setSelectedStore(null)}>
        <DialogContent className="bg-white border border-black/10 text-black max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              {selectedStore?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-gray-400 text-sm">{selectedStore?.category}</p>
            {selectedStore?.benefits && selectedStore.benefits.length > 0 ? (
              <div className="space-y-2">
                {selectedStore.benefits.map((benefit, index) => (
                  <div key={benefit.id || index} className="bg-black/5 rounded-2xl p-4 border border-black/8">
                    <div className="flex items-start gap-3">
                      <Gift className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium text-sm">{benefit.title}</p>
                        {benefit.description && (
                          <p className="text-gray-500 text-xs mt-1">{benefit.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Sin beneficios disponibles</p>
            )}
            <p className="text-gray-400 text-xs text-center">
              Muestra tu QR en tienda · Exclusivo para miembros VONDI
            </p>
            <Button
              onClick={() => { setSelectedStore(null); navigate("/signup"); }}
              className="w-full bg-black text-white rounded-full h-12 font-semibold hover:bg-black/90 active:scale-95 transition-all"
            >
              Activar mi acceso
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Detalle de beneficio ── */}
      <Dialog open={!!selectedBenefit} onOpenChange={() => setSelectedBenefit(null)}>
        <DialogContent className="bg-white border border-black/10 text-black max-w-md rounded-3xl">
          <div className="space-y-5 pt-2">
            {/* Nombre de tienda */}
            <p className="text-gray-400 text-[10px] font-semibold tracking-widest uppercase text-center">
              {selectedBenefit?.storeName || "Tienda"}
            </p>

            {/* Logo */}
            {selectedBenefit && getStoreLogo(selectedBenefit) && (
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-2xl overflow-hidden bg-black/5 border border-black/8 flex items-center justify-center">
                  <img
                    src={getStoreLogo(selectedBenefit)}
                    alt={selectedBenefit.storeName || "Logo"}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            )}

            {/* Chip oscuro con título del beneficio */}
            <div className="bg-black rounded-2xl px-4 py-3 mx-auto" style={{ maxWidth: "85%" }}>
              <p className="text-center text-white text-sm font-semibold leading-tight">
                {selectedBenefit?.title?.toUpperCase()}
              </p>
            </div>

            {/* Info — T&C propios de la promoción (Módulo 1), visibles también
                antes de iniciar sesión. Sin términos capturados, texto genérico. */}
            {selectedBenefit?.terms ? (
              <div className="bg-black/5 rounded-2xl p-4 border border-black/8 space-y-1.5">
                <p className="text-gray-600 text-[11px] font-semibold uppercase tracking-wider text-center mb-1">
                  Términos y condiciones
                </p>
                <p className="text-gray-500 text-xs text-center whitespace-pre-line leading-relaxed">
                  {selectedBenefit.terms}
                </p>
                <p className="text-gray-500 text-xs text-center pt-1">
                  Muestra tu QR para validar el beneficio
                </p>
              </div>
            ) : (
              <div className="bg-black/5 rounded-2xl p-4 border border-black/8 space-y-1.5">
                {[
                  "Consulta términos y condiciones en tienda",
                  "Muestra tu QR para validar el beneficio",
                  "Exclusivo para miembros VONDI",
                ].map((txt) => (
                  <p key={txt} className="text-gray-500 text-xs text-center">
                    {txt}
                  </p>
                ))}
              </div>
            )}

            {/* CTA */}
            <Button
              onClick={() => navigate("/login")}
              className="w-full bg-black text-white rounded-full h-12 font-semibold hover:bg-black/90 active:scale-95 transition-all"
            >
              Acceder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoresPreview;
